import { useEffect, useRef, useState } from "react";

import type { MobManoeuvre } from "@nav-games/mob";

interface MobFigureProps {
  manoeuvre: MobManoeuvre;
  size?: number;
  /// When set, the animation runs once on mount. Toggle the
  /// `playKey` prop to a new value to replay.
  playKey?: number | string;
}

const SEA = "#dde7ef";
const TRACK = "#1e6091";
const CASUALTY = "#c0382b";
const WIND_FROM = "#94a3b8";

/// Top-down animated diagram for a man-overboard recovery
/// manoeuvre. The boat traces the manoeuvre's waypoint path while
/// a step caption updates underneath. The wind direction is fixed
/// at 0° (north) so the helmsman terminology — "bear off",
/// "tack" — reads the same way every time.
export function MobFigure({
  manoeuvre,
  size = 320,
  playKey,
}: MobFigureProps) {
  const [t, setT] = useState(0);
  const rafRef = useRef<number | null>(null);
  const startedAt = useRef<number>(performance.now());

  useEffect(() => {
    setT(0);
    startedAt.current = performance.now();
    const tick = () => {
      const elapsed = performance.now() - startedAt.current;
      const nextT = Math.min(1, elapsed / manoeuvre.durationMs);
      setT(nextT);
      if (nextT < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [manoeuvre.id, manoeuvre.durationMs, playKey]);

  // Find the current segment along the path. Each segment occupies
  // an equal share of t in [0,1] for simplicity.
  const n = manoeuvre.path.length;
  const segCount = Math.max(1, n - 1);
  const segIdx = Math.min(segCount - 1, Math.floor(t * segCount));
  const segT = (t * segCount) - segIdx;
  const a = manoeuvre.path[segIdx]!;
  const b = manoeuvre.path[Math.min(n - 1, segIdx + 1)]!;
  const x = a.x + (b.x - a.x) * segT;
  const y = a.y + (b.y - a.y) * segT;
  const heading = lerpAngle(a.heading, b.heading, segT);

  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      style={{ display: "block", background: SEA, borderRadius: 8 }}
      role="img"
      aria-label={manoeuvre.name}
    >
      {/* Faint grid */}
      <g stroke="#cbd6df" strokeWidth="0.3" strokeDasharray="0.6 1">
        {[20, 40, 60, 80].map((g) => (
          <line key={`h${g}`} x1="0" y1={g} x2="100" y2={g} />
        ))}
        {[20, 40, 60, 80].map((g) => (
          <line key={`v${g}`} x1={g} y1="0" x2={g} y2="100" />
        ))}
      </g>

      {/* Wind arrow top-left — wind blows FROM the north (top) for
          every scenario so the helmsman terms read consistently. */}
      <g transform="translate(8 8)">
        <line x1="0" y1="-4" x2="0" y2="8" stroke={WIND_FROM} strokeWidth="0.8" />
        <polygon points="-2,5 2,5 0,9" fill={WIND_FROM} />
        <text
          x="0"
          y="-6"
          textAnchor="middle"
          fontSize="3.5"
          fill={WIND_FROM}
          fontFamily="ui-sans-serif, system-ui"
        >
          Wind
        </text>
      </g>

      {/* Full intended track — drawn as a faint polyline */}
      <polyline
        points={manoeuvre.path.map((p) => `${p.x},${p.y}`).join(" ")}
        fill="none"
        stroke={TRACK}
        strokeOpacity="0.25"
        strokeWidth="0.6"
        strokeDasharray="1 1"
      />

      {/* Travelled track up to the boat's current position */}
      <polyline
        points={[
          ...manoeuvre.path.slice(0, segIdx + 1).map((p) => `${p.x},${p.y}`),
          `${x},${y}`,
        ].join(" ")}
        fill="none"
        stroke={TRACK}
        strokeWidth="0.8"
        strokeLinejoin="round"
        strokeLinecap="round"
      />

      {/* MOB casualty marker */}
      <g transform={`translate(${manoeuvre.mob.x} ${manoeuvre.mob.y})`}>
        <circle r="2.5" fill={CASUALTY} stroke="#7a1f17" strokeWidth="0.4" />
        <text
          y="-4"
          textAnchor="middle"
          fontSize="3.5"
          fill={CASUALTY}
          fontFamily="ui-sans-serif, system-ui"
          fontWeight="700"
        >
          MOB
        </text>
      </g>

      {/* Boat sprite */}
      <g transform={`translate(${x} ${y}) rotate(${heading})`}>
        <polygon
          points="0,-3.5 2.2,3 -2.2,3"
          fill="#1c2530"
          stroke="#0f172a"
          strokeWidth="0.4"
        />
        {/* Sail indicator for sailing manoeuvres */}
        {a.sail && a.sail !== "n/a" && (
          <line
            x1="0"
            y1="-3"
            x2={sailOffset(a.sail).x}
            y2={sailOffset(a.sail).y}
            stroke="#f5f5f5"
            strokeWidth="0.6"
          />
        )}
      </g>
    </svg>
  );
}

function lerpAngle(a: number, b: number, t: number): number {
  // Shortest-arc interpolation for headings — handles 350° → 10°
  // without spinning the wrong way.
  let diff = ((b - a + 540) % 360) - 180;
  return a + diff * t;
}

function sailOffset(point: NonNullable<MobManoeuvre["path"][number]["sail"]>): {
  x: number;
  y: number;
} {
  switch (point) {
    case "close-hauled":
      return { x: 1.5, y: 0 };
    case "close-reach":
      return { x: 2, y: 0.5 };
    case "beam-reach":
      return { x: 2.2, y: 1.5 };
    case "broad-reach":
      return { x: 2, y: 2.5 };
    case "run":
      return { x: 0, y: 3 };
    case "luffing":
      return { x: 0, y: -1 };
    case "n/a":
      return { x: 0, y: 0 };
  }
}
